import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const WishlistContext = createContext(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // wishlistIds: array of product _id strings (for quick lookup)
  const [wishlistIds, setWishlistIds] = useState([]);
  // wishlistItems: full product objects (for the Wishlist page)
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistIds([]);
      setWishlistItems([]);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get('/wishlist');
      const items = res.data?.data || [];
      setWishlistItems(items);
      setWishlistIds(items.map(p => p._id));
    } catch (error) {
      // Silently fail — wishlist is non-critical
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching wishlist:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const isInWishlist = useCallback(
    (productId) => wishlistIds.includes(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(async (productId, productName) => {
    if (!isAuthenticated) {
      toast.error('Please log in to use your wishlist');
      return;
    }

    const alreadyIn = wishlistIds.includes(productId);

    // Optimistic update
    if (alreadyIn) {
      setWishlistIds(prev => prev.filter(id => id !== productId));
      setWishlistItems(prev => prev.filter(p => p._id !== productId));
    } else {
      setWishlistIds(prev => [...prev, productId]);
    }

    try {
      if (alreadyIn) {
        await api.delete(`/wishlist/${productId}`);
        toast.success(productName ? `${productName} removed from wishlist` : 'Removed from wishlist');
      } else {
        await api.post(`/wishlist/${productId}`);
        toast.success(productName ? `${productName} added to wishlist` : 'Added to wishlist');
        // Re-fetch to get full product details for the new item
        fetchWishlist();
      }
    } catch (error) {
      // Revert optimistic update on failure
      fetchWishlist();
      toast.error('Failed to update wishlist');
    }
  }, [isAuthenticated, wishlistIds, fetchWishlist]);

  const value = {
    wishlistIds,
    wishlistItems,
    wishlistCount: wishlistIds.length,
    loading,
    isInWishlist,
    toggleWishlist,
    fetchWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
